import { CurrencyPipe, DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { LocalizationService } from '../../../core/services/localization.service';
import {
  AttendanceEventType,
  AttendanceMessage,
  AttendanceMessageType,
} from '../interfaces/attendance-list-history.interface';
import { ChannelMessageTextParserService } from '../services/channel-message-text-parser.service';

@Injectable({ providedIn: 'root' })
export class AttendanceHistoryAdapterService {
  constructor(
    private translate: LocalizationService,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private channelMessageTextParser: ChannelMessageTextParserService
  ) {}

  adapt(attendanceHistory: AttendanceMessage[]): AttendanceMessage[] {
    return attendanceHistory.filter(Boolean).map(this.parseMessage.bind(this));
  }

  private parseMessage(m: AttendanceMessage) {
    if (m.type === AttendanceMessageType.EVENT) {
      this.parseEvent(m);
    } else if (m.type === AttendanceMessageType.MESSAGE) {
      this.channelMessageTextParser.adaptIncomingMessages(m);
    }
    return m;
  }

  private parseEvent(message: AttendanceMessage) {
    if (message.type !== AttendanceMessageType.EVENT || !message.data.type) {
      return;
    }

    this.translateEventTerms(message);
    this.parseEventDates(message);
    this.parseEventMoney(message);
    this.removeHotelReservationIdIfUndefined(message);
  }

  private translateEventTerms(message: AttendanceMessage) {
    const TRANSLATABLE_TERMS = ['channel'];

    TRANSLATABLE_TERMS.forEach((key) => {
      if (message.data.content[key]) {
        let translationKey = '';

        if (key === 'channel') {
          message.data.content.rawChannel = message.data.content[key];
          translationKey = `CHANNELS.${message.data.content[key]}`;
        }

        message.data.content[key] = this.translate.key(translationKey);
      }
    });
  }

  private parseEventDates(message: AttendanceMessage) {
    const fullDate = this.translate.DATE_FORMAT_FULL_WITH_SECONDS;
    const dateOnly = this.translate.DATE_FORMAT_DATE_ONLY;
    const monthName = this.translate.DATE_MONTH_NAME_ONLY;
    const DATE_TERMS = [
      { key: 'createdAt', format: fullDate },
      { key: 'starterDate', format: dateOnly },
      {
        key: 'month',
        format: monthName,
        locale: this.translate.selectedLanguage || 'en',
      },
    ];

    if (message.data.type === AttendanceEventType.ACTION_RESERVATION_AUTOMATICALLY_EXPEDIA) {
      DATE_TERMS.push(
        { key: 'departureDate', format: dateOnly },
        { key: 'arrivalDate', format: dateOnly }
      );
    }

    DATE_TERMS.forEach(({ key, format, locale }) => {
      if (message.data?.content[key]) {
        const parsedDate = this.datePipe.transform(
          message.data.content[key],
          format,
          undefined,
          locale
        );
        message.data.content[key] = this.parseDateInfo(key, parsedDate);
      }
    });
  }

  private parseDateInfo(key: string, dateInfo?: string | null): string {
    if (!dateInfo) {
      return '';
    }

    if (key === 'month') {
      return this.capitalize(dateInfo);
    }

    return dateInfo;
  }

  private capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private parseEventMoney(message: AttendanceMessage) {
    if (!message?.data?.content) return;

    const {
      totalPrice,
      currency,
      companyStandardCurrency,
      standardCurrencyValue,
    } = message.data.content;

    if (!totalPrice || !currency) return;

    const locale = this.translate.selectedLanguage || 'en';

    // If reservation currency is different from company currency
    if (
      companyStandardCurrency &&
      standardCurrencyValue &&
      currency !== companyStandardCurrency
    ) {
      const convertedPrice = this.currencyPipe.transform(
        standardCurrencyValue,
        companyStandardCurrency,
        'symbol',
        '1.2-2',
        locale
      );
      const originalPrice = this.currencyPipe.transform(
        totalPrice,
        currency,
        'symbol',
        '1.2-2',
        locale
      );

      message.data.content.parsedPrice = `${convertedPrice} (${originalPrice})`;
    }

    // If reservation currency is equal to company currency (or company currency data is not provided)
    else {
      message.data.content.parsedPrice = this.currencyPipe.transform(
        totalPrice,
        currency,
        'symbol',
        '1.2-2',
        locale
      );
    }
  }

  private removeHotelReservationIdIfUndefined(message: AttendanceMessage) {
    const reservationEventsWithHotelId = [
      AttendanceEventType.ACTION_RESERVATION_MANUALLY_CONFIRMED,
      AttendanceEventType.ACTION_RESERVATION_MANUALLY_CONFIRMED_TRAVELER,
      AttendanceEventType.ACTION_RESERVATION_MANUALLY_CONFIRMED_GROUP,
    ];

    if (!reservationEventsWithHotelId.includes(message.data.type as AttendanceEventType)) {
      return;
    }

    // message.data.content.hotelReservationId = undefined;

    const hotelReservationId = message.data?.content?.hotelReservationId;
    
    // Create a dynamic text that will be empty if hotelReservationId is undefined
    if (hotelReservationId && hotelReservationId !== 'undefined') {
      const reservationCodeLabel = this.translate.key('RESERVATION_CODE');
      message.data.content.hotelReservationIdText = `<br>${reservationCodeLabel}: <b>${hotelReservationId}</b>`;
    } else {
      message.data.content.hotelReservationIdText = '';
    }
  }
}
